import { useEffect, useRef } from 'react';
import {
  useReactFlow,
  useStore,
  Node,
  Edge,
  ReactFlowState,
  Position
} from '@xyflow/react';
import { stratify, tree, HierarchyNode } from 'd3-hierarchy';
import { timer } from 'd3-timer';
import { useAppStore } from '../stores/UnifiedStoreProvider';

// Priority weights for sorting
interface PriorityWeights {
  high: number;
  medium: number;
  low: number;
}

const PRIORITY_WEIGHT: PriorityWeights = {
  high: 0,
  medium: 1,
  low: 2
};

// Create layout with dynamic settings based on preferences
function createLayout(compactness: number, nodeSpacing: number) {
  // Apply compactness multiplier to spacing
  const verticalSpacing = 180 / compactness; // Height between nodes
  const horizontalSpacing = nodeSpacing; // Width between levels
  
  return tree<Node>()
    .nodeSize([verticalSpacing, horizontalSpacing])
    .separation((a, b) => {
      // More separation for nodes with many children
      const aChildren = a.children?.length || 0;
      const bChildren = b.children?.length || 0;
      const maxChildren = Math.max(aChildren, bChildren);
      
      if (a.parent === b.parent) {
        // Siblings need more space if they have many children
        return (1 + (maxChildren * 0.1)) / compactness;
      }
      return 2 / compactness; // More separation for non-siblings
    });
}

const options = { duration: 150 }; // Faster animation

/**
 * Sort function to arrange nodes by priority and necessity within each level
 * Priority is the primary sort (High → Medium → Low), then necessity (Necessary → Sufficient)
 * This ensures high priority nodes appear at the top of their level in the DAG
 */
function sortNodesByPriority(a: HierarchyNode<Node>, b: HierarchyNode<Node>): number {
  const aData = a.data.data || {};
  const bData = b.data.data || {};
  
  const aPriority = (aData.priority as keyof PriorityWeights) || 'medium';
  const bPriority = (bData.priority as keyof PriorityWeights) || 'medium';
  
  // Get necessity values
  const aIsNecessary = aData.isNecessary === true;
  const bIsNecessary = bData.isNecessary === true;
  
  // First, sort by priority (lower weight = higher in layout)
  const priorityDiff = PRIORITY_WEIGHT[aPriority] - PRIORITY_WEIGHT[bPriority];
  if (priorityDiff !== 0) {
    return priorityDiff;
  }
  
  // If priorities are equal, sort by necessity (necessary nodes higher)
  if (aIsNecessary && !bIsNecessary) return -1;
  if (!aIsNecessary && bIsNecessary) return 1;
  
  return 0;
}

// Layout function that accepts nodes, edges, and layout preferences
function layoutNodes(nodes: Node[], edges: Edge[], compactness: number = 1.0, nodeSpacing: number = 150): Node[] {
  console.log('[layoutNodes] Starting with', nodes.length, 'nodes, compactness:', compactness, 'spacing:', nodeSpacing);
  if (nodes.length === 0) {
    return [];
  }
  
  try {
    // Debug node data
    console.log('[layoutNodes] First node data:', nodes[0].data);
    
    // Find root nodes - nodes without parentId or marked as root
    const rootNodes = nodes.filter(node => 
      !node.data?.parentId || node.data?.isRoot === true
    );
    
    console.log(`[layoutNodes] Found ${rootNodes.length} root nodes:`, 
      rootNodes.map(n => ({ id: n.id, label: n.data?.label })));
    
    // Handle multiple roots by creating a virtual root
    let layoutNodes = [...nodes];
    let virtualRootId: string | null = null;
    
    if (rootNodes.length > 1) {
      virtualRootId = 'virtual-root-' + Date.now();
      const virtualRoot: Node = {
        id: virtualRootId,
        position: { x: -400, y: 0 },
        data: { label: 'Virtual Root', virtual: true },
        type: 'workflow',
        hidden: true
      };
      
      layoutNodes = [virtualRoot, ...nodes];
    } else if (rootNodes.length === 0) {
      console.warn('No root nodes found, using first node as root');
      if (nodes.length > 0) {
        const firstNode = nodes[0];
        virtualRootId = 'virtual-root-' + Date.now();
        const virtualRoot: Node = {
          id: virtualRootId,
          position: { x: -400, y: 0 },
          data: { label: 'Virtual Root', virtual: true },
          type: 'workflow',
          hidden: true
        };
        
        layoutNodes = [virtualRoot, ...nodes];
      }
    }
    
    // Create hierarchy using d3-hierarchy with parentId from node data
    const hierarchy = stratify<Node>()
      .id(d => d.id)
      .parentId(d => {
        // Use parentId from node data if available
        if (d.data?.parentId) {
          return d.data.parentId;
        }
        
        // For root nodes with virtual root
        if (virtualRootId && (!d.data?.parentId || d.data?.isRoot)) {
          return d.id === virtualRootId ? null : virtualRootId;
        }
        
        // Fallback to edge-based parent detection
        const parent = edges.find(e => e.target === d.id);
        return parent?.source || null;
      })(layoutNodes);
    
    // Sort children by priority
    hierarchy.sort(sortNodesByPriority);
    
    // Create layout with custom settings
    const dynamicLayout = createLayout(compactness, nodeSpacing);
    
    // Apply tree layout
    const root = dynamicLayout(hierarchy);
    
    // Convert back to nodes with updated positions
    return nodes.map(node => {
      const hierarchyNode = root.descendants().find(d => d.data.id === node.id);
      
      if (!hierarchyNode) {
        return node;
      }
      
      // For left-to-right layout, swap x and y
      return {
        ...node,
        position: {
          x: hierarchyNode.y || 0,
          y: hierarchyNode.x || 0
        },
        targetPosition: Position.Left,
        sourcePosition: Position.Right
      };
    });
  } catch (error) {
    console.error('Layout error:', error);
    return nodes;
  }
}

// Store selector for nodes - triggers layout when nodes change
const nodeSelector = (state: ReactFlowState) => {
  // Track node IDs, priority, necessity, AND parentId to trigger re-layout when these change
  // Don't track positions to avoid layout loops
  const nodes = Array.from(state.nodeLookup.values());
  return nodes.map(n => 
    `${n.id}:${n.data?.priority || 'medium'}:${n.data?.isNecessary || false}:${n.data?.parentId || 'root'}`
  ).sort().join('|');
};

// Export a function to trigger layout manually (for re-parenting)
export function triggerLayout() {
  // This will be called by the component after successful re-parenting
  // We'll use a custom event to trigger the layout
  window.dispatchEvent(new CustomEvent('mindmap:forceLayout'));
}

// Custom hook for applying layout to mindmap
export function useMindmapLayout(enabled: boolean = true) {
  const initial = useRef(true);
  const layoutTimeoutRef = useRef<NodeJS.Timeout>();
  const lastNodeHashRef = useRef<string>('');
  const forceLayoutRef = useRef(0);

  // Get layout preferences
  const appStore = useAppStore();
  const { preferences } = appStore;

  // Use node changes as trigger for re-layouting
  const nodeHash = useStore(nodeSelector);
  
  const { getNodes, getEdges, setNodes, getNode, fitView } = useReactFlow();
  const animationFrameRef = useRef<any>();
  
  // Listen for manual layout triggers
  useEffect(() => {
    const handleForceLayout = () => {
      console.log('🎨 [Layout Hook] Force layout triggered');
      forceLayoutRef.current += 1;
    };
    
    window.addEventListener('mindmap:forceLayout', handleForceLayout);
    return () => window.removeEventListener('mindmap:forceLayout', handleForceLayout);
  }, []);

  // Track preferences changes separately to trigger re-layout
  const lastCompactnessRef = useRef(preferences.mindmapCompactness);
  const lastSpacingRef = useRef(preferences.mindmapNodeSpacing);
  const lastNodeSizeRef = useRef(preferences.mindmapNodeSize);
  
  useEffect(() => {
    // Check if preferences changed (force layout even if node structure didn't change)
    const preferencesChanged = 
      lastCompactnessRef.current !== preferences.mindmapCompactness ||
      lastSpacingRef.current !== preferences.mindmapNodeSpacing ||
      lastNodeSizeRef.current !== preferences.mindmapNodeSize;
    
    if (preferencesChanged) {
      console.log('🎨 [Layout Hook] Preferences changed, forcing layout:', {
        compactness: preferences.mindmapCompactness,
        spacing: preferences.mindmapNodeSpacing,
        nodeSize: preferences.mindmapNodeSize
      });
      lastCompactnessRef.current = preferences.mindmapCompactness;
      lastSpacingRef.current = preferences.mindmapNodeSpacing;
      lastNodeSizeRef.current = preferences.mindmapNodeSize;
      forceLayoutRef.current += 1; // Force layout
    }
    
    // Skip if hash hasn't changed AND no force layout requested
    if (nodeHash === lastNodeHashRef.current && forceLayoutRef.current === 0) {
      return;
    }
    lastNodeHashRef.current = nodeHash;
    
    // Reset force layout flag
    if (forceLayoutRef.current > 0) {
      console.log('🎨 [Layout Hook] Processing forced layout request');
      forceLayoutRef.current = 0;
    }
    
    console.log('🎨 [Layout Hook] Node hash changed:', nodeHash?.substring(0, 50));
    
    if (!enabled) {
      console.log('🎨 [Layout Hook] Skipping - not enabled');
      return;
    }
    
    // Clear any pending layout
    if (layoutTimeoutRef.current) {
      clearTimeout(layoutTimeoutRef.current);
    }
    
    // Debounce layout calculation (shorter delay for more responsive feel)
    layoutTimeoutRef.current = setTimeout(() => {
    
    // Get current nodes and edges from React Flow
    const nodes = getNodes();
    const edges = getEdges();
    
    // Don't skip for temp nodes - we need to layout them too!
    const hasTempNodes = nodes.some(n => n.id.startsWith('temp-'));
    if (hasTempNodes) {
      console.log('🎨 [Layout] Has temp nodes - will layout anyway');
    }
    
    console.log('🎨 [Layout] Current state:', {
      nodes: nodes.map(n => ({
        id: n.id,
        label: n.data?.label,
        parentId: n.data?.parentId,
        position: n.position
      })),
      edges: edges.map(e => ({
        source: e.source,
        target: e.target
      }))
    });
    
    console.log('[Layout Hook] Got', nodes.length, 'nodes and', edges.length, 'edges');
    console.log('[Layout Hook] First node:', nodes[0]);
    
    if (nodes.length === 0) {
      console.log('[Layout Hook] Skipping - no nodes');
        return;
    }
    
    console.log('🎨 [Layout Hook] Calculating new layout...');

    // Calculate new positions with user preferences
    const targetNodes = layoutNodes(nodes, edges, preferences.mindmapCompactness, preferences.mindmapNodeSpacing);
    console.log('🎨 [Layout Hook] Layout calculated, first node position:', targetNodes[0]?.position);
    console.log('🎨 [Layout Hook] All calculated positions:', targetNodes.map(n => ({ 
      id: n.id, 
      label: n.data?.label,
      parentId: n.data?.parentId,
      pos: n.position 
    })));
    
    // Create transition objects with current and target positions
    const transitions = targetNodes.map((node) => {
      return {
        id: node.id,
        from: getNode(node.id)?.position || node.position,
        to: node.position,
        node,
      };
    });

    console.log('[Layout Hook] Starting animation...');
    
    // Animate nodes to their new positions
    const t = timer((elapsed: number) => {
      const s = elapsed / options.duration;

      const currNodes = transitions.map(({ node, from, to }) => {
        return {
          ...node,
          position: {
            // Linear interpolation
            x: from.x + (to.x - from.x) * s,
            y: from.y + (to.y - from.y) * s,
          },
        };
      });

      setNodes(currNodes);

      // Final step of animation
      if (elapsed > options.duration) {
        // Move nodes to exact destination
        const finalNodes = transitions.map(({ node, to }) => {
          return {
            ...node,
            position: {
              x: to.x,
              y: to.y,
            },
          };
        });

        setNodes(finalNodes);

        // Stop animation
        t.stop();
        
        // Fit view after initial layout
        if (!initial.current) {
          fitView({ duration: 200, padding: 0.2 });
        }
        initial.current = false;
        
        console.log('[Layout Hook] Animation complete');
      }
    });
    
    animationFrameRef.current = t;
    }, 50); // 50ms debounce for responsive slider updates

    return () => {
      if (animationFrameRef.current) {
        animationFrameRef.current.stop();
      }
      if (layoutTimeoutRef.current) {
        clearTimeout(layoutTimeoutRef.current);
      }
    };
  }, [nodeHash, enabled, getNodes, getEdges, setNodes, getNode, fitView, forceLayoutRef.current, preferences.mindmapCompactness, preferences.mindmapNodeSpacing, preferences.mindmapNodeSize]);
}

export default useMindmapLayout; 